/**
 * OptionsAPI Class
 * Handles fetching product sections with option value selections
 * Uses Shopify's Section Rendering API with option_values parameter
 */
class OptionsAPI {
  /**
   * Fetch a product section with selected option values
   * @param {string} productUrl - The product URL
   * @param {string} sectionId - The section ID to fetch
   * @param {string[]} optionValueIds - Array of selected option value IDs
   * @returns {Promise<Document>} Parsed HTML document
   */
  static async fetchSection(productUrl, sectionId, optionValueIds = []) {
    // Build query parameters with option_value IDs
    const params = optionValueIds.length > 0
      ? `&option_values=${optionValueIds.join(',')}`
      : '';
    
    const url = `${productUrl}?section_id=${sectionId}${params}`;
    
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      return html;
    } catch (error) {
      console.error('[HIGH VARIANT] OptionsAPI.fetchSection - Error fetching section:', error);
      throw error;
    }
  }

  /**
   * Extract option value selectors from parsed HTML
   * @param {Document} html - Parsed HTML document
   * @returns {Element|null} The optionValueSelectors element
   */
  static getOptionValueSelectors(html) {
    return html.querySelector('.optionValueSelectors');
  }

  /**
   * Extract product section from parsed HTML
   * @param {Document} html - Parsed HTML document
   * @param {string} sectionId - The section ID
   * @returns {Element|null} The product section element
   */
  static getProductSection(html, sectionId) {
    return html.querySelector(`.shopify-section[id="shopify-section-${sectionId}"]`);
  }
}

/**
 * High Variant Picker Component
 * Supports high-variant products using Shopify's new APIs
 * Liquid-based approach - only fetches section when option values change
 * Based on ia-variant-picker.js pattern
 */
class HighVariantPicker extends HTMLElement {
  connectedCallback() {
    this.optionValueSelectors = this.querySelector('.optionValueSelectors');
    this._selects = null; // Cache for selects
    this._boundHandleChange = this.handleVariantChange.bind(this);
    
    // Single event listener on the component for all select changes
    this.addEventListener('change', this._boundHandleChange);
  }

  /**
   * Get all option selects (cached)
   */
  getSelects() {
    if (!this._selects) {
      this._selects = Array.from(this.querySelectorAll('select[name^="options["]'));
    }
    return this._selects;
  }

  /**
   * Extract selected option data needed for fetch
   * Returns: { optionValueIds, productUrl }
   */
  getSelectedOptionData() {
    const selects = this.getSelects();
    const data = {
      optionValueIds: [],
      productUrl: null
    };
    
    // Iterate once to collect all data
    for (let i = selects.length - 1; i >= 0; i--) {
      const select = selects[i];
      const selectedOption = select.options[select.selectedIndex];
      if (!selectedOption) continue;
      
      // Collect option value ID
      const optionValueId = selectedOption.dataset.optionValueId;
      if (optionValueId) {
        data.optionValueIds.unshift(optionValueId); // Add to beginning to maintain order
      }
      
      // Get product URL (combined listings) - last one wins
      const productUrl = selectedOption.dataset.productUrl;
      if (productUrl && productUrl !== '') {
        data.productUrl = productUrl;
      }
    }
    
    return data;
  }

  async handleVariantChange(event) {
    // Get all selected option data in one pass
    const optionData = this.getSelectedOptionData();
    
    // Get current product URL (for combined listings support)
    const oldProductUrl = this.dataset.url || window.location.pathname;
    const newProductUrl = optionData.productUrl || this.dataset.url || window.location.pathname;
    
    // Fetch and render - form replacement handles variant ID automatically
    await this.fetchAndRender(optionData.optionValueIds, newProductUrl, oldProductUrl);
  }

  /**
   * Fetch section data and render (render method is empty for now)
   */
  async fetchAndRender(optionValueIds, newProductUrl, oldProductUrl) {
    const sectionId = this.sectionId;
    if (!sectionId) {
      return;
    }
    
    try {
      // Fetch the section
      const html = await OptionsAPI.fetchSection(newProductUrl, sectionId, optionValueIds);
      
      // Render - replaces elements with [data-re-render] and returns current variant
      const currentVariant = this.render(html, sectionId, newProductUrl, oldProductUrl);
      
      // Update URL if needed with new variant ID
      if (this.updateUrl && currentVariant?.id) {
        this.updateURL(currentVariant.id);
      }
      
      // Dispatch variant:change event matching global.js structure
      if (currentVariant) {
        document.dispatchEvent(new CustomEvent('variant:change', {
          detail: {
            variant: currentVariant,
            section: sectionId,
            url: this.url
          }
        }));
      }
      
    } catch (error) {
      console.error('[HIGH VARIANT] HighVariantPicker.fetchAndRender - Error:', error);
    }
  }

  /**
   * Render method - replaces all elements with [data-re-render] in current section
   * @param {Document} html - Parsed HTML from fetch
   * @param {string} sectionId - Section ID
   * @param {string} newProductUrl - New product URL
   * @param {string} oldProductUrl - Old product URL
   * @returns {Object|null} Current variant object from the rendered section
   */
  render(html, sectionId, newProductUrl, oldProductUrl) {
    // Find the current section
    const shopifySection = this.closest('.shopify-section');
    if (!shopifySection) {
      return null;
    }

    // Get new variant ID from the new high-variant-picker element before replacing
    const newHighVariantPicker = html.querySelector('high-variant-picker');
    const newVariantId = newHighVariantPicker?.dataset.currentVariantId || null;

    // Parse current variant JSON from the new HTML
    const variantScript = newHighVariantPicker?.querySelector('script[data-current-variant]');
    let currentVariant = null;
    if (variantScript) {
      try {
        currentVariant = JSON.parse(variantScript.textContent);
      } catch (error) {
        console.error('[HIGH VARIANT] HighVariantPicker.render - Error parsing variant JSON:', error);
      }
    }

    // Find all elements with [data-re-render] in the current section
    const elementsToRerender = shopifySection.querySelectorAll('[data-re-render]');
    
    // Replace each element with the corresponding element from the new HTML
    elementsToRerender.forEach(element => {
      const elementId = element.id;
      if (!elementId) return;
      
      // Find the corresponding element in the new HTML by ID
      const newElement = html.querySelector(`#${elementId}`);
      if (newElement) {
        element.outerHTML = newElement.outerHTML;
      }
    });
    
    // Update the current variant ID attribute on this element
    if (newVariantId) {
      this.dataset.currentVariantId = newVariantId;
    }
    
    return currentVariant;
  }

  getCurrentVariantId() {
    // Get variant ID from data-current-variant-id attribute
    return this.dataset.currentVariantId || null;
  }

  updateURL(variantId = null) {
    const id = variantId || this.getCurrentVariantId();
    if (id && this.url) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', id);
      window.history.replaceState({}, '', url);
    }
  }

  get sectionId() {
    return this.dataset.section;
  }

  get productId() {
    return this.dataset.productId || null;
  }

  get updateUrl() {
    return this.dataset.updateUrl !== 'false';
  }

  get url() {
    return this.dataset.url || window.location.pathname;
  }
}

if (!customElements.get('high-variant-picker')) {
  customElements.define('high-variant-picker', HighVariantPicker);
}


